import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import {
  getBaseFields,
  getEntityCreateUrl,
  getEntityUpdateUrl,
  getEntityDetailUrl,
  getEntityDeleteUrl,
} from '../config/entityFormConfig'
import { translateApiError } from '../utils/errorMessages'
import { amountAfterVat } from '../utils/vatCalculations'
import { normalizeDate } from '../utils/dateUtils'
import { buildActBudgetOptions, actBudgetPayload } from '../utils/actBudgetOptions'
import { Button, FormGroup, Input, Select } from './ui'
import ConfirmModal from './ConfirmModal'
import './EntityForm.css'

const DADATA_DEBOUNCE_MS = 400
const DADATA_MIN_QUERY = 2
const DADATA_ADDRESS_DEBOUNCE_MS = 350
const DADATA_ADDRESS_MIN_QUERY = 2

const ENTITY_REF_LIMIT = 10000
const OBJECT_COST_CALC_INPUT_KEYS = ['living_area', 'parking_spaces', 'estimated_square_price', 'estimated_parking_price']
const OBJECT_COST_CALC_SKIP_KEYS = ['parking_spaces', 'estimated_square_price', 'estimated_parking_price', 'estimated_contract_price', 'estimated_contract_price_no_vat']

const REF_TYPE_DISTRIBUTION = 'тип распределения'

function referenceItemMatchesDistributionType(item, distributionType) {
  const want = (distributionType || '').trim()
  if (!want) return false
  const fromName = (item?.name || '').trim()
  const fromCode = (item?.code || '').trim()
  const fromId = String(item?.id ?? '').trim()
  return fromName === want || fromCode === want || fromId === want
}

function getReferenceItemValue(item) {
  if (item == null) return ''
  if (typeof item === 'string' || typeof item === 'number') return String(item)
  return item.name || item.code || String(item.id ?? '')
}

function EntityForm({ entityType, entityId, context = {}, title, successRedirect }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({})
  const [customData, setCustomData] = useState({})
  const [customDefinitions, setCustomDefinitions] = useState([])
  const [references, setReferences] = useState({})
  const [customReferences, setCustomReferences] = useState({}) // { refId: [values] }
  const [entityOptions, setEntityOptions] = useState({}) // { clients: [], objects: [], stages: [] }
  const [percentageGroups, setPercentageGroups] = useState([])
  const [dadataSuggestions, setDadataSuggestions] = useState([])
  const [dadataLoading, setDadataLoading] = useState(false)
  const [dadataOpen, setDadataOpen] = useState(false)
  const [dadataError, setDadataError] = useState(null)
  const dadataWrapperRef = useRef(null)
  const dadataJustAppliedRef = useRef(false)
  const dadataQueryFieldRef = useRef('name')
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressOpen, setAddressOpen] = useState(false)
  const [addressError, setAddressError] = useState(null)
  const addressWrapperRef = useRef(null)
  const addressJustAppliedRef = useRef(false)
  const [costAuto, setCostAuto] = useState(true) // объект: рассчитывать стоимость с НДС автоматически
  const costAutoRef = useRef(true)
  const isEdit = !!entityId
  const baseFields = getBaseFields(entityType)
  const isClientsCreate = entityType === 'clients' && !isEdit
  const isObjectsAddress = entityType === 'objects' // Dadata для адреса объекта

  const hasCustomFieldsPermission = user?.permissions?.['custom_fields.view']
  const deletePermissionCode = `${entityType}.delete`
  const hasDeleteFromPerms = !!user?.permissions?.[deletePermissionCode]
  const hasDeleteUrl = !!getEntityDeleteUrl(entityType, entityId)
  const hasDeletePermission = isEdit && hasDeleteUrl && (hasDeleteFromPerms || user?.role === 'admin' || user?.role === 'management')
  const [deleting, setDeleting] = useState(false)
  const [createNextModal, setCreateNextModal] = useState({ show: false, entityType: null, createdId: null, created: null })
  const [creatingDefaultStages, setCreatingDefaultStages] = useState(false)
  const [objectSystemParams, setObjectSystemParams] = useState({ vat_percent: 5, default_price_per_sqm: 0, default_price_per_parking: 0 })
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      let actStageId = null
      let objectSystemParamsLoaded = { vat_percent: 5, default_price_per_sqm: 0, default_price_per_parking: 0 }
      try {
        if (entityType === 'objects' || entityType === 'stages') {
          try {
            const sysRes = await api.get('/settings/system-params')
            const d = sysRes.data
            if (d) {
              objectSystemParamsLoaded = {
                vat_percent: Number(d.vat_percent) || 5,
                default_price_per_sqm: Number(d.default_price_per_sqm) || 0,
                default_price_per_parking: Number(d.default_price_per_parking) || 0
              }
              if (!cancelled) setObjectSystemParams(objectSystemParamsLoaded)
            }
          } catch (_) {}
        }
        let mergeStageDistributionType = ''
        if (isEdit) {
          const url = getEntityDetailUrl(entityType, entityId, context)
          if (!url) {
            setError('Неизвестный тип сущности')
            return
          }
          const res = await api.get(url.replace('/api', ''))
          const data = res.data?.[entityType.slice(0, -1)] || res.data?.client || res.data?.object || res.data?.contract || res.data?.stage || res.data?.act
          if (!data) {
            setError('Запись не найдена')
            return
          }
          if (!cancelled) {
            const initial = {}
            baseFields.forEach((f) => {
              if (data[f.key] !== undefined && data[f.key] !== null) {
                initial[f.key] = f.type === 'date' ? normalizeDate(data[f.key]) : data[f.key]
              }
            })
            if (entityType === 'acts' && !initial.budget_id && data.stage_id) actStageId = data.stage_id
            if (entityType === 'objects' && data.image_url !== undefined) initial.image_url = data.image_url
            if (entityType === 'objects' && data.cost_manual !== undefined) initial.cost_manual = data.cost_manual
            if (isObjectsAddress && initial.address) addressJustAppliedRef.current = true
            setFormData(initial)
            setCustomData(data.custom_data || {})
            if (entityType === 'objects' && data.cost_manual) setCostAuto(false)
            if (entityType === 'stages') {
              mergeStageDistributionType = (initial.distribution_type || '').trim()
            }
          }
        } else {
          const initial = {}
          baseFields.forEach((f) => {
            initial[f.key] = f.defaultValue ?? (f.type === 'number' ? '' : '')
          })
          if (entityType === 'stages' && location.state?.stageDraft && typeof location.state.stageDraft === 'object') {
            Object.assign(initial, location.state.stageDraft)
          }
          if (context.clientId && (entityType === 'objects' || entityType === 'contracts')) {
            initial.client_id = context.clientId
          }
          if (context.objectId && entityType === 'contracts') {
            initial.object_id = context.objectId
          }
          if (entityType === 'objects') {
            initial.estimated_square_price = objectSystemParamsLoaded.default_price_per_sqm
            initial.estimated_parking_price = objectSystemParamsLoaded.default_price_per_parking
            initial.cost_manual = false
          }
          setFormData(initial)
          if (entityType === 'stages' && location.state?.stageCustomDraft && typeof location.state.stageCustomDraft === 'object') {
            setCustomData(location.state.stageCustomDraft)
          } else {
            setCustomData({})
          }
          if (entityType === 'stages') {
            mergeStageDistributionType = (initial.distribution_type || '').trim()
          }
        }

        if (hasCustomFieldsPermission) {
          try {
            const defRes = await api.get(`/admin/custom-fields/definitions?entity_type=${entityType}`)
            if (!cancelled && defRes.data?.definitions) {
              setCustomDefinitions(defRes.data.definitions)
            }
          } catch (e) {
            if (!cancelled) setCustomDefinitions([])
          }
        }

        try {
          const refRes = await api.get('/admin/reference')
          if (!cancelled && refRes.data?.references) {
            const refs = { ...refRes.data.references }
            if (entityType === 'stages' && mergeStageDistributionType) {
              const list = Array.isArray(refs[REF_TYPE_DISTRIBUTION]) ? [...refs[REF_TYPE_DISTRIBUTION]] : []
              const exists = list.some((item) => referenceItemMatchesDistributionType(item, mergeStageDistributionType))
              if (!exists) {
                list.push({
                  id: mergeStageDistributionType,
                  name: mergeStageDistributionType,
                  code: mergeStageDistributionType
                })
              }
              refs[REF_TYPE_DISTRIBUTION] = list
            }
            setReferences(refs)
          }
          if (!cancelled && refRes.data?.custom_references) {
            const map = {}
            refRes.data.custom_references.forEach((r) => { map[r.id] = r.values || [] })
            setCustomReferences(map)
          }
        } catch (e) {
          if (!cancelled) setReferences({})
          if (!cancelled) setCustomReferences({})
        }

        const refs = {}
        for (const f of baseFields) {
          if (f.type === 'entity_ref' && f.entityRef) {
            const listUrl = getListUrlForRef(f)
            if (listUrl) {
              try {
                const listRes = await api.get(listUrl.replace('/api', ''))
                const key = f.entityRef
                const arr = listRes.data?.[key] || listRes.data?.clients || listRes.data?.objects || listRes.data?.contracts || listRes.data?.stages || listRes.data?.acts || []
                refs[key] = arr
              } catch (e) {
                refs[f.entityRef] = []
              }
            }
          }
        }
        if (entityType === 'stages' && context.contractId) {
          try {
            const contractRes = await api.get(`/company/contracts/${encodeURIComponent(context.contractId)}`)
            const objectId = contractRes.data?.contract?.object_id
            if (objectId) {
              const objectStagesRes = await api.get(
                `/company/objects/${encodeURIComponent(objectId)}/stages?limit=${ENTITY_REF_LIMIT}`,
              )
              const stagesList = objectStagesRes.data?.stages || []
              refs.object_stages = stagesList.filter((stage) => !stage.is_archived)
            } else {
              refs.object_stages = []
            }
          } catch (e) {
            refs.object_stages = []
          }
        }
        if (entityType === 'stages') {
          try {
            const groupsRes = await api.get('/admin/budget/percentage-groups/options')
            const groups = groupsRes.data?.groups || []
            if (!cancelled) {
              setPercentageGroups(groups)
              if (!isEdit) {
                const current = groups.find((g) => g.is_current) || groups[0]
                if (current) {
                  setFormData((prev) => prev.base_percentage_group_id ? prev : { ...prev, base_percentage_group_id: current.id })
                }
              }
            }
          } catch (e) {
            if (!cancelled) setPercentageGroups([])
          }
        }
        if (entityType === 'acts' && context.contractId) {
          const stagesRes = await api.get(`/company/contracts/${encodeURIComponent(context.contractId)}/stages?limit=${ENTITY_REF_LIMIT}`)
          refs.budgets = buildActBudgetOptions(refs.budgets, stagesRes.data?.stages || [])
        }
        if (!cancelled) {
          setEntityOptions(refs)
          if (entityType === 'acts' && (actStageId || (!isEdit && context.legacyStageId))) {
            const selectedStageId = actStageId || context.legacyStageId
            const budget = (refs.budgets || []).find((item) => String(item.legacy_stage_id) === String(selectedStageId))
            if (budget) setFormData((prev) => ({ ...prev, budget_id: String(budget.id) }))
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(translateApiError(e.response?.data?.error || e.message) || 'Ошибка загрузки')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    function getListUrlForRef(field) {
      if (field.entityRef === 'budgets') {
        const params = []
        if (context.contractId) params.push(`contract_id=${encodeURIComponent(context.contractId)}`)
        if (context.objectId) params.push(`object_id=${encodeURIComponent(context.objectId)}`)
        if (context.stageId) params.push(`stage_id=${encodeURIComponent(context.stageId)}`)
        return `/api/company/budgets${params.length ? `?${params.join('&')}` : ''}`
      }
      if (field.entityRef === 'stages' && context.objectId) {
        return `/api/company/objects/${context.objectId}/stages?limit=${ENTITY_REF_LIMIT}`
      }
      if (field.entityRef === 'object_stages' && context.objectId) {
        return `/api/company/objects/${context.objectId}/stages?limit=${ENTITY_REF_LIMIT}`
      }
      if (field.entityRef === 'stages' && context.contractId) {
        return `/api/company/contracts/${context.contractId}/stages?limit=${ENTITY_REF_LIMIT}`
      }
      if (field.entityRef === 'acts' && context.contractId) {
        return `/api/company/contracts/${context.contractId}/acts?limit=${ENTITY_REF_LIMIT}`
      }
      if (field.entityRef === 'clients') return `/api/company/clients?limit=${ENTITY_REF_LIMIT}`
      if (field.entityRef === 'objects') return `/api/company/objects?limit=${ENTITY_REF_LIMIT}`
      if (field.entityRef === 'contracts') return `/api/company/contracts?limit=${ENTITY_REF_LIMIT}`
      return null
    }
    load()
    return () => { cancelled = true }
  }, [entityType, entityId, isEdit, hasCustomFieldsPermission, context.contractId, context.clientId, context.objectId, context.stageId, context.legacyStageId, location.state])

  useEffect(() => {
    if (isEdit || entityType !== 'contracts') return
    setFormData((prev) => {
      let changed = false
      const next = { ...prev }
      baseFields.forEach((field) => {
        if (
          field.defaultValue != null
          && field.defaultValue !== ''
          && (prev[field.key] == null || prev[field.key] === '')
        ) {
          next[field.key] = field.defaultValue
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [entityType, isEdit, baseFields, references])

  useEffect(() => {
    if (entityType !== 'stages') return
    const createdDistributionType = (location.state?.stageDraft?.distribution_type || '').trim()
    if (!createdDistributionType) return
    setReferences((prev) => {
      const current = prev[REF_TYPE_DISTRIBUTION] || []
      const exists = current.some((item) => referenceItemMatchesDistributionType(item, createdDistributionType))
      if (exists) return prev
      return {
        ...prev,
        [REF_TYPE_DISTRIBUTION]: [
          ...current,
          { id: createdDistributionType, name: createdDistributionType, code: createdDistributionType }
        ]
      }
    })
  }, [entityType, location.state])

  // Dadata: подсказки организаций по ИНН/названию (только форма создания клиента)
  useEffect(() => {
    if (!isClientsCreate) {
      setDadataOpen(false)
      return
    }
    if (dadataJustAppliedRef.current) {
      dadataJustAppliedRef.current = false
      setDadataOpen(false)
      setDadataSuggestions([])
      return
    }
    const query = (dadataQueryFieldRef.current === 'inn' ? (formData.inn || '') : (formData.name || '')).trim().replace(/\s+/g, ' ')
    if (query.length < DADATA_MIN_QUERY) {
      setDadataSuggestions([])
      setDadataOpen(false)
      setDadataError(null)
      return
    }
    const t = setTimeout(async () => {
      setDadataLoading(true)
      setDadataError(null)
      try {
        const res = await api.post('/dadata/suggest/party', { query, count: 10 })
        const list = res.data?.suggestions || []
        setDadataSuggestions(list)
        setDadataOpen(list.length > 0)
      } catch (e) {
        setDadataSuggestions([])
        setDadataOpen(false)
        const msg = e.response?.data?.error || e.message || 'Не удалось загрузить подсказки'
        setDadataError(translateApiError(msg))
      } finally {
        setDadataLoading(false)
      }
    }, DADATA_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [isClientsCreate, formData.name, formData.inn])

  // Закрытие выпадающего списка Dadata по клику снаружи
  useEffect(() => {
    if (!dadataOpen) return
    const onMouseDown = (e) => {
      if (dadataWrapperRef.current && !dadataWrapperRef.current.contains(e.target)) {
        setDadataOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [dadataOpen])

  useEffect(() => {
    costAutoRef.current = costAuto
  }, [costAuto])

  // Dadata: подсказки адресов (форма клиента и форма объекта — Адрес и Город)
  useEffect(() => {
    const needAddressDadata = isClientsCreate || isObjectsAddress
    if (!needAddressDadata) {
      setAddressOpen(false)
      return
    }
    if (addressJustAppliedRef.current) {
      addressJustAppliedRef.current = false
      setAddressOpen(false)
      setAddressSuggestions([])
      return
    }
    const query = (formData.address || '').trim().replace(/\s+/g, ' ')
    if (query.length < DADATA_ADDRESS_MIN_QUERY) {
      setAddressSuggestions([])
      setAddressOpen(false)
      setAddressError(null)
      return
    }
    const t = setTimeout(async () => {
      setAddressLoading(true)
      setAddressError(null)
      try {
        const res = await api.post('/dadata/suggest/address', { query, count: 10 })
        const list = res.data?.suggestions || []
        setAddressSuggestions(list)
        setAddressOpen(list.length > 0)
      } catch (e) {
        setAddressSuggestions([])
        setAddressOpen(false)
        setAddressError(translateApiError(e.response?.data?.error || e.message) || 'Не удалось загрузить подсказки адреса')
      } finally {
        setAddressLoading(false)
      }
    }, DADATA_ADDRESS_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [isClientsCreate, isObjectsAddress, formData.address])

  useEffect(() => {
    if (!addressOpen) return
    const onMouseDown = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setAddressOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [addressOpen])

  const applyAddressSuggestion = (suggestion) => {
    if (!suggestion?.data) return
    addressJustAppliedRef.current = true
    const addr = suggestion.value || suggestion.unrestricted_value || ''
    const city = suggestion.data.city || suggestion.data.settlement || suggestion.data.area || suggestion.data.region || ''
    setFormData((prev) => ({ ...prev, address: addr, city: city || prev.city }))
    setAddressOpen(false)
    setAddressSuggestions([])
  }

  const applyDadataSuggestion = (suggestion) => {
    const d = suggestion?.data
    if (!d) return
    dadataJustAppliedRef.current = true
    const updates = {}
    if (d.name?.short_with_opf || d.name?.short) updates.name = d.name.short_with_opf || d.name.short
    if (d.inn) updates.inn = d.inn
    if (d.ogrn) updates.ogrn = d.ogrn
    if (d.opf?.short) updates.type = d.opf.short
    if (d.address?.value) updates.address = d.address.value
    const city = d.address?.data?.city || d.address?.data?.settlement
    if (city) updates.city = city
    setFormData((prev) => ({ ...prev, ...updates }))
    setDadataOpen(false)
    setDadataSuggestions([])
  }

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value }
      if (entityType === 'objects' && OBJECT_COST_CALC_INPUT_KEYS.includes(key)) {
        if (costAutoRef.current) {
          const la = key === 'living_area' ? (value === '' || value === null ? 0 : Number(value)) : (prev.living_area === '' || prev.living_area === null ? 0 : Number(prev.living_area))
          const ps = key === 'parking_spaces' ? (value === '' || value === null ? 0 : Number(value)) : (prev.parking_spaces === '' || prev.parking_spaces === null ? 0 : Number(prev.parking_spaces))
          const sq = key === 'estimated_square_price' ? (value === '' || value === null ? 0 : Number(value)) : (prev.estimated_square_price === '' || prev.estimated_square_price === null ? 0 : Number(prev.estimated_square_price))
          const pk = key === 'estimated_parking_price' ? (value === '' || value === null ? 0 : Number(value)) : (prev.estimated_parking_price === '' || prev.estimated_parking_price === null ? 0 : Number(prev.estimated_parking_price))
          const withVat = la * sq + ps * pk
          const vat = objectSystemParams.vat_percent / 100
          next.estimated_contract_price = withVat
          next.estimated_contract_price_no_vat = Math.round((withVat / (1 + vat)) * 100) / 100
        }
      }
      if (entityType === 'objects' && key === 'estimated_contract_price') {
        const withVat = value === '' || value === null ? null : Number(value)
        const vatPct = Number(objectSystemParams.vat_percent) || 5
        next.estimated_contract_price_no_vat = withVat != null ? amountAfterVat(withVat, vatPct) : null
      }
      if (entityType === 'stages' && key === 'cost') {
        const c = value === '' || value === null ? null : Number(value)
        const vatPct = Number(objectSystemParams.vat_percent) || 5
        next.cost_no_vat = amountAfterVat(c, vatPct)
      }
      if (entityType === 'stages' && key === 'contract_cost') {
        const cc = value === '' || value === null ? null : Number(value)
        const vatPct = Number(objectSystemParams.vat_percent) || 5
        next.contract_cost_no_vat = amountAfterVat(cc, vatPct)
      }
      if (entityType === 'acts' && key === 'payment_type') {
        next.status = value === 'cash' ? 'не оплачен' : value === 'artel' ? null : 'не подписан'
        next.number = ''
        next.amount_after_tax = value === 'bank_transfer' ? amountAfterVat(prev.amount, Number(objectSystemParams.vat_percent) || 5) : prev.amount
      }
      if (entityType === 'acts' && key === 'amount') {
        const a = value === '' || value === null ? null : Number(value)
        const vatPct = Number(objectSystemParams.vat_percent) || 5
        next.amount_after_tax = (prev.payment_type || 'bank_transfer') === 'bank_transfer' ? amountAfterVat(a, vatPct) : a
      }
      return next
    })
  }

  const handleDadataNameChange = (e) => {
    dadataQueryFieldRef.current = 'name'
    handleChange('name', e.target.value)
  }

  const handleDadataInnChange = (e) => {
    dadataQueryFieldRef.current = 'inn'
    handleChange('inn', e.target.value)
  }

  const handleCustomChange = (code, value) => {
    setCustomData((prev) => ({ ...prev, [code]: value }))
  }

  const handleSubmit = async (e, { recalc = false } = {}) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload = entityType === 'acts'
        ? actBudgetPayload({ ...formData, custom_data: customData })
        : { ...formData, custom_data: customData }
      if (entityType === 'contracts') {
        if (!payload.status) payload.status = 'новый'
        if (!payload.budget_status) payload.budget_status = 'новый'
      }
      if (recalc) payload.recalc = true
      let createdId = null
      if (isEdit) {
        const { method, url } = getEntityUpdateUrl(entityType, entityId)
        await api.request({ method, url: url.replace('/api', ''), data: payload })
      } else {
        const createSpec = getEntityCreateUrl(entityType, context)
        if (!createSpec) {
          setError('Создание для этой сущности недоступно')
          setSaving(false)
          return
        }
        const res = await api.request({ method: createSpec.method, url: createSpec.url.replace('/api', ''), data: payload })
        const singleKey = entityType.slice(0, -1)
        const created = res.data?.[singleKey] || res.data?.client || res.data?.object || res.data?.contract || res.data?.stage || res.data?.act || res.data?.contact
        if (created?.id) createdId = created.id
        if (!successRedirect && createdId && (
          entityType === 'clients'
          || entityType === 'objects'
          || (entityType === 'contracts' && created?.object_id)
        )) {
          setCreateNextModal({ show: true, entityType, createdId, created: created || null })
          setSaving(false)
          return
        }
      }
      if (successRedirect) {
        navigate(successRedirect)
      } else if (entityType === 'clients') {
        navigate(isEdit ? `/client/${entityId}` : (createdId ? `/client/${createdId}` : '/company/clients'))
      } else if (entityType === 'contacts' && context.clientId) {
        navigate(`/client/${context.clientId}`)
      } else if (entityType === 'objects') {
        if (context.objectId) {
          navigate(`/object/${context.objectId}`)
        } else if (context.clientId) {
          navigate(`/client/${context.clientId}`)
        } else {
          navigate(isEdit ? `/object/${entityId}` : (createdId ? `/object/${createdId}` : '/company/objects'))
        }
      } else if (entityType === 'contracts') {
        if (context.objectId) {
          navigate(`/object/${context.objectId}`)
        } else if (context.clientId) {
          navigate(`/client/${context.clientId}`)
        } else {
          navigate(isEdit ? `/contract/${entityId}` : (createdId ? `/contract/${createdId}` : '/company/contracts'))
        }
      } else if (entityType === 'stages' && context.contractId) {
        navigate(`/contract/${context.contractId}`)
      } else if (entityType === 'acts' && context.contractId) {
        navigate(`/contract/${context.contractId}`)
      } else {
        navigate('/company/clients')
      }
    } catch (e) {
      const errMsg = e.response?.data?.error
      const detailsMsg = e.response?.data?.details
      if (detailsMsg) console.error('API details:', detailsMsg)
      const raw = errMsg || e.message || 'Ошибка сохранения'
      setError(translateApiError(typeof raw === 'string' ? raw : String(raw)))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить? Это действие нельзя отменить.')) return
    const spec = getEntityDeleteUrl(entityType, entityId)
    if (!spec) return
    setDeleting(true)
    setError(null)
    try {
      await api.request({ method: spec.method, url: spec.url.replace('/api', '') })
      if (successRedirect) {
        navigate(successRedirect)
      } else if (entityType === 'clients') {
        navigate('/company/clients')
      } else if (entityType === 'contacts' && context.clientId) {
        navigate(`/client/${context.clientId}`)
      } else if (entityType === 'objects') {
        if (context.objectId) navigate(`/object/${context.objectId}`)
        else if (context.clientId) navigate(`/client/${context.clientId}`)
        else navigate('/company/objects')
      } else if (entityType === 'contracts') {
        if (context.objectId) navigate(`/object/${context.objectId}`)
        else if (context.clientId) navigate(`/client/${context.clientId}`)
        else navigate('/company/contracts')
      } else if ((entityType === 'stages' || entityType === 'acts') && context.contractId) {
        navigate(`/contract/${context.contractId}`)
      } else {
        navigate(-1)
      }
    } catch (e) {
      setError(translateApiError(e.response?.data?.error || e.message) || 'Не удалось удалить')
    } finally {
      setDeleting(false)
    }
  }

  const renderField = (field, id) => {
    const value = formData[field.key]
    const onChange = (v) => handleChange(field.key, v)
    if (field.type === 'text') {
      return (
        <Input
          id={id}
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
        />
      )
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          id={id}
          className="ui-input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          rows={3}
        />
      )
    }
    if (field.type === 'number') {
      return (
        <Input
          id={id}
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
          required={field.required}
        />
      )
    }
    if (field.type === 'date') {
      return (
        <Input
          id={id}
          type="date"
          value={normalizeDate(value)}
          onChange={(e) => onChange(e.target.value || null)}
          required={field.required}
          disabled={field.readOnly}
        />
      )
    }
    if (field.type === 'boolean') {
      return (
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
      )
    }
    if (field.type === 'select' && Array.isArray(field.options)) {
      return (
        <Select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          required={field.required}
          disabled={field.readOnly}
        >
          <option value="">— Выберите —</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label ?? opt.value}
            </option>
          ))}
        </Select>
      )
    }
    if (field.type === 'entity_ref' && field.entityRef) {
      const options = entityOptions[field.entityRef] || []
      let list = Array.isArray(options) ? options : []
      if (field.filterByRef && formData[field.filterByRef]) {
        list = list.filter((item) => item.client_id === formData[field.filterByRef])
      }
      const valueKey = 'id'
      const getItemLabel = (item) => {
        if (field.entityRef === 'object_stages') {
          const title = item.name || item.type || item.id
          if (item.name && item.type && item.type !== item.name) {
            return `${item.name} (${item.type})`
          }
          return title
        }
        if (field.entityRef === 'stages') return item.name ?? item.id
        if (field.entityRef === 'budgets') return item.name ?? item.id
        if (field.entityRef === 'clients' || field.entityRef === 'objects') return item.name ?? item.id
        return item.number ?? item.name ?? item.id
      }
      const handleRefChange = (e) => {
        const val = e.target.value || null
        onChange(val)
        if (entityType === 'contracts' && field.key === 'object_id' && val) {
          const obj = list.find((o) => o.id === val)
          if (obj?.client_id) setFormData((prev) => ({ ...prev, client_id: obj.client_id }))
        }
      }
      return (
        <Select
          id={id}
          value={value ?? ''}
          onChange={handleRefChange}
          required={field.required}
          disabled={field.readOnly}
        >
          <option value="">— Выберите —</option>
          {list.map((item) => (
            <option key={item[valueKey]} value={item[valueKey]}>
              {getItemLabel(item)}
            </option>
          ))}
        </Select>
      )
    }
    if (field.type === 'percentage_group_ref') {
      return (
        <Select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          required={field.required}
          disabled={field.readOnly}
        >
          <option value="">— действующая на момент создания —</option>
          {percentageGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.label}{group.is_current ? ' · Действующая' : ''}
            </option>
          ))}
        </Select>
      )
    }
    if (field.type === 'reference' && field.referenceType) {
      let list = references[field.referenceType] || []
      if (field.referenceOrder) {
        const orderMap = Object.fromEntries(field.referenceOrder.map((name, i) => [name.toLowerCase(), i]))
        list = [...list].filter((item) => {
          const value = getReferenceItemValue(item).toLowerCase()
          return Object.prototype.hasOwnProperty.call(orderMap, value)
        }).sort((a, b) => {
          const ia = orderMap[getReferenceItemValue(a).toLowerCase()] ?? 999
          const ib = orderMap[getReferenceItemValue(b).toLowerCase()] ?? 999
          return ia - ib
        })
      }
      const isStagesDistributionType = entityType === 'stages' && field.referenceType === REF_TYPE_DISTRIBUTION
      return (
        <div>
          <Select
            id={id}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
            required={field.required}
          >
            <option value="">— Выберите —</option>
            {list.map((item, index) => {
              const itemValue = getReferenceItemValue(item)
              return (
                <option key={item?.id ?? `${itemValue}-${index}`} value={itemValue}>
                  {itemValue}
                </option>
              )
            })}
          </Select>
          {isStagesDistributionType && (
            <Button
              type="button"
              variant="ghost"
              className="link-button"
              style={{ marginTop: 'var(--spacing-xs)' }}
              onClick={() => navigate('/admin/budget/budget-rules/new', {
                state: {
                  returnTo: location.pathname + location.search,
                  stageDraft: formData,
                  stageCustomDraft: customData
                }
              })}
            >
              + Создать новый тип
            </Button>
          )}
        </div>
      )
    }
    return (
      <Input
        id={id}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
      />
    )
  }

  const renderCustomField = (def, id) => {
    const value = customData[def.code]
    const onChange = (v) => handleCustomChange(def.code, v)
    if (def.field_type === 'string') {
      return (
        <Input
          id={id}
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={def.is_required}
        />
      )
    }
    if (def.field_type === 'number') {
      return (
        <Input
          id={id}
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
          required={def.is_required}
        />
      )
    }
    if (def.field_type === 'boolean') {
      return (
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
      )
    }
    if (def.field_type === 'reference') {
      let options
      if (def.reference_type) {
        options = (references[def.reference_type] || []).map((r) => ({ value: r.id, label: r.name || r.code }))
      } else if (def.custom_reference_id) {
        const vals = customReferences[def.custom_reference_id] || []
        options = vals.map((v) => ({ value: v.id, label: v.label || v.value }))
      } else {
        options = (def.options || []).map((o) => ({ value: o.value, label: o.label }))
      }
      return (
        <Select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          required={def.is_required}
        >
          <option value="">— Выберите —</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      )
    }
    return (
      <Input
        id={id}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={def.is_required}
      />
    )
  }

  const PAIR_NO_VAT_KEYS = {
    cost: 'cost_no_vat',
    contract_cost: 'contract_cost_no_vat',
    amount: 'amount_after_tax',
  }

  const renderStageCostPair = (mainField, mainId) => {
    const noVatKey = PAIR_NO_VAT_KEYS[mainField.key] || (mainField.key + '_no_vat')
    const noVatField = baseFields.find(f => f.key === noVatKey)
    const vatPct = Number(objectSystemParams.vat_percent) || 5
    const noVatRaw = formData[noVatKey]
    const noVatDisplay = noVatRaw != null && noVatRaw !== ''
      ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(noVatRaw))
      : '—'
    return (
      <div key={`pair-${mainField.key}`} className="entity-form-field-pair">
        <FormGroup label={mainField.label} required={mainField.required} htmlFor={mainId} hint={mainField.hint}>
          <Input id={mainId} type="number" value={formData[mainField.key] ?? ''} onChange={(e) => handleChange(mainField.key, e.target.value === '' ? '' : parseFloat(e.target.value))} required={mainField.required} />
        </FormGroup>
        <FormGroup label={noVatField?.label || 'За вычетом налогов'} required={noVatField?.required} htmlFor={`field-${noVatKey}`} hint={noVatField?.hint}>
          <div className="entity-form-readonly-with-vat">
            <Input id={`field-${noVatKey}`} type="text" value={noVatDisplay} readOnly disabled className="entity-form-readonly-input" />
            <span className="entity-form-vat-hint">НДС {vatPct}%</span>
          </div>
        </FormGroup>
      </div>
    )
  }

  if (loading) {
    return <div className="entity-form-loading">Загрузка...</div>
  }

  return (
    <div className="entity-form-card">
      <form onSubmit={handleSubmit} className="entity-form">
        <h2>{title || (isEdit ? 'Редактирование' : 'Создание')}</h2>
        {error && <div className="entity-form-error">{error}</div>}
        <div className="entity-form-fields">
          {baseFields.map((baseField) => {
            let field = baseField
            if (entityType === 'acts') {
              const kind = formData.payment_type || 'bank_transfer'
              if (field.key === 'payment_type') field = { ...field, readOnly: isEdit }
              if (kind !== 'bank_transfer') {
                if (['number', 'amount_after_tax'].includes(field.key)) return null
                if (field.key === 'status') {
                  if (kind === 'artel') return null
                  field = { ...field, options: [{ value: 'не оплачен', label: 'не оплачен' }, { value: 'оплачен', label: 'оплачен' }] }
                }
                if (field.key === 'issue_date') field = { ...field, required: true }
                if (field.key === 'amount') field = { ...field, hint: 'Фактическая сумма без НДС' }
              }
            }
            if (field.hidden) return null
            const id = `field-${field.key}`
            if (isClientsCreate && field.key === 'name') {
              return (
                <div key={field.key} className="form-group dadata-wrapper" ref={dadataWrapperRef}>
                  <label htmlFor={id}>
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>
                  <input
                    id={id}
                    type="text"
                    value={formData.name ?? ''}
                    onChange={handleDadataNameChange}
                    onFocus={() => dadataSuggestions.length > 0 && setDadataOpen(true)}
                    required={field.required}
                    placeholder="Начните вводить название или ИНН"
                    autoComplete="off"
                  />
                  {dadataError && (
                    <div className="dadata-error" role="alert">
                      {dadataError}
                    </div>
                  )}
                  {dadataOpen && (
                    <div className="dadata-dropdown">
                      {dadataLoading ? (
                        <div className="dadata-loading">Загрузка...</div>
                      ) : (
                        <ul className="dadata-list">
                          {dadataSuggestions.map((s, i) => (
                            <li
                              key={s.data?.inn || s.value || i}
                              className="dadata-item"
                              onMouseDown={(e) => { e.preventDefault(); applyDadataSuggestion(s) }}
                            >
                              <span className="dadata-item-name">{s.value}</span>
                              {s.data?.inn && <span className="dadata-item-inn">ИНН {s.data.inn}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )
            }
            if (isClientsCreate && field.key === 'inn') {
              return (
                <div key={field.key} className="form-group">
                  <label htmlFor={id}>{field.label}</label>
                  <input
                    id={id}
                    type="text"
                    value={formData.inn ?? ''}
                    onChange={handleDadataInnChange}
                    onFocus={() => dadataSuggestions.length > 0 && setDadataOpen(true)}
                    placeholder="Или введите ИНН для подсказок"
                    autoComplete="off"
                  />
                </div>
              )
            }
            if ((entityType === 'clients' || entityType === 'objects') && field.key === 'address') {
              return (
                <div key={field.key} className="form-group dadata-wrapper" ref={addressWrapperRef}>
                  <label htmlFor={id}>{field.label}</label>
                  <input
                    id={id}
                    type="text"
                    value={formData.address ?? ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setAddressOpen(true)}
                    placeholder="Начните вводить адрес — подсказка заполнит адрес и город"
                    autoComplete="off"
                  />
                  {addressError && (
                    <div className="dadata-error" role="alert">
                      {addressError}
                    </div>
                  )}
                  {addressOpen && (
                    <div className="dadata-dropdown">
                      {addressLoading ? (
                        <div className="dadata-loading">Загрузка...</div>
                      ) : (
                        <ul className="dadata-list">
                          {addressSuggestions.map((s, i) => (
                            <li
                              key={s.data?.fias_id || s.value || i}
                              className="dadata-item"
                              onMouseDown={(e) => { e.preventDefault(); applyAddressSuggestion(s) }}
                            >
                              <span className="dadata-item-name">{s.value}</span>
                              {(s.data?.city || s.data?.settlement) && (
                                <span className="dadata-item-inn">
                                  {s.data.city || s.data.settlement}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )
            }
            if (entityType === 'objects' && OBJECT_COST_CALC_SKIP_KEYS.includes(field.key)) {
              return null
            }
            if (entityType === 'objects' && field.key === 'living_area') {
              const vatPct = Number(objectSystemParams.vat_percent) || 5
              const noVatVal = formData.estimated_contract_price_no_vat
              const noVatDisplay = noVatVal != null && noVatVal !== ''
                ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(noVatVal))
                : '—'
              return (
                <div key="object-cost-calculator" className="entity-form-cost-calculator">
                  <h3 className="entity-form-cost-calculator-title">Калькулятор стоимости</h3>
                  <div className="entity-form-cost-calculator-grid">
                    <FormGroup label="Площадь (м²)" htmlFor="field-living_area">
                      <Input
                        id="field-living_area"
                        type="number"
                        min={0}
                        value={formData.living_area ?? ''}
                        onChange={(e) => handleChange('living_area', e.target.value === '' ? '' : parseFloat(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup label="Машиномест" htmlFor="field-parking_spaces">
                      <Input
                        id="field-parking_spaces"
                        type="number"
                        min={0}
                        value={formData.parking_spaces ?? ''}
                        onChange={(e) => handleChange('parking_spaces', e.target.value === '' ? '' : parseFloat(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup label="Цена за м² (₽)" htmlFor="field-estimated_square_price">
                      <Input
                        id="field-estimated_square_price"
                        type="number"
                        min={0}
                        value={formData.estimated_square_price ?? ''}
                        onChange={(e) => handleChange('estimated_square_price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                      />
                    </FormGroup>
                    <FormGroup label="Цена м/место, ₽" htmlFor="field-estimated_parking_price">
                      <Input
                        id="field-estimated_parking_price"
                        type="number"
                        min={0}
                        value={formData.estimated_parking_price ?? ''}
                        onChange={(e) => handleChange('estimated_parking_price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                      />
                    </FormGroup>
                  </div>
                  <div className="entity-form-cost-calculator-totals">
                    <div className="entity-form-cost-calculator-row">
                      <label className="entity-form-cost-calculator-checkbox">
                        <input
                          type="checkbox"
                          checked={costAuto}
                          onChange={(e) => {
                            const checked = e.target.checked
                            setCostAuto(checked)
                            handleChange('cost_manual', !checked)
                            if (checked) {
                              setFormData((prev) => {
                                const la = prev.living_area === '' || prev.living_area === null ? 0 : Number(prev.living_area)
                                const ps = prev.parking_spaces === '' || prev.parking_spaces === null ? 0 : Number(prev.parking_spaces)
                                const sq = prev.estimated_square_price === '' || prev.estimated_square_price === null ? 0 : Number(prev.estimated_square_price)
                                const pk = prev.estimated_parking_price === '' || prev.estimated_parking_price === null ? 0 : Number(prev.estimated_parking_price)
                                const withVat = la * sq + ps * pk
                                const vat = objectSystemParams.vat_percent / 100
                                return {
                                  ...prev,
                                  estimated_contract_price: withVat,
                                  estimated_contract_price_no_vat: Math.round((withVat / (1 + vat)) * 100) / 100
                                }
                              })
                            }
                          }}
                        />
                        <span>Рассчитать автоматически</span>
                      </label>
                    </div>
                    <div className="entity-form-cost-calculator-row entity-form-cost-calculator-amounts">
                      <FormGroup label="Стоимость с НДС (₽)" htmlFor="field-estimated_contract_price">
                        {costAuto ? (
                          <Input
                            id="field-estimated_contract_price"
                            type="text"
                            value={formData.estimated_contract_price != null && formData.estimated_contract_price !== ''
                              ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(formData.estimated_contract_price))
                              : '—'}
                            readOnly
                            disabled
                            className="entity-form-readonly-input"
                          />
                        ) : (
                          <Input
                            id="field-estimated_contract_price"
                            type="number"
                            min={0}
                            value={formData.estimated_contract_price ?? ''}
                            onChange={(e) => handleChange('estimated_contract_price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          />
                        )}
                      </FormGroup>
                      <FormGroup label="Стоимость без НДС (₽)" htmlFor="field-estimated_contract_price_no_vat">
                        <div className="entity-form-readonly-with-vat">
                          <Input
                            id="field-estimated_contract_price_no_vat"
                            type="text"
                            value={noVatDisplay}
                            readOnly
                            disabled
                            className="entity-form-readonly-input"
                          />
                          <span className="entity-form-vat-hint">НДС {vatPct}%</span>
                        </div>
                      </FormGroup>
                    </div>
                  </div>
                </div>
              )
            }
            if (entityType === 'stages' && (field.key === 'cost' || field.key === 'contract_cost')) {
              return renderStageCostPair(field, id)
            }
            if (entityType === 'stages' && (field.key === 'cost_no_vat' || field.key === 'contract_cost_no_vat')) {
              return null
            }
            if (entityType === 'acts' && field.key === 'amount' && (formData.payment_type || 'bank_transfer') === 'bank_transfer') {
              return renderStageCostPair(field, id)
            }
            if (entityType === 'acts' && field.key === 'amount_after_tax') {
              return null
            }
            return (
              <React.Fragment key={field.key}>
                <FormGroup label={field.label} required={field.required} htmlFor={id} title={field.title} hint={field.hint}>
                  {renderField(field, id)}
                </FormGroup>
              </React.Fragment>
            )
          })}
          {customDefinitions.length > 0 && (
            <>
              <h3 className="entity-form-custom-title">Доп. информация</h3>
              {customDefinitions.map((def) => (
                <FormGroup key={def.id} label={def.label} required={def.is_required} htmlFor={`custom-${def.code}`}>
                  {renderCustomField(def, `custom-${def.code}`)}
                </FormGroup>
              ))}
            </>
          )}
        </div>
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={() => navigate(-1)} disabled={saving || deleting}>
            Отмена
          </Button>
          {hasDeletePermission && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={saving || deleting}
            >
              {deleting ? 'Удаление...' : 'Удалить'}
            </Button>
          )}
          {isEdit && (entityType === 'stages' || entityType === 'acts') ? (
            <>
              <Button type="submit" variant="secondary" disabled={saving || deleting}>
                {saving ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={saving || deleting}
                onClick={(e) => handleSubmit(e, { recalc: true })}
                title={entityType === 'stages'
                  ? 'Сохранить изменения и пересчитать распределение доходов по этой стадии и её актам'
                  : 'Сохранить изменения и пересчитать распределение доходов по акту'}
              >
                {saving ? 'Сохранение...' : 'Сохранить с пересчётом распределения'}
              </Button>
            </>
          ) : (
            <Button type="submit" variant="primary" disabled={saving || deleting}>
              {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
            </Button>
          )}
        </div>
      </form>
      {createNextModal.show && createNextModal.entityType === 'clients' && (
        <ConfirmModal
          title="Создать объект?"
          message={<p>Создать объект для этого клиента?</p>}
          cancelLabel="Нет, перейти к карточке клиента"
          confirmLabel="Да, создать объект"
          confirmVariant="primary"
          onCancel={() => {
            setCreateNextModal((m) => ({ ...m, show: false }))
            navigate(`/client/${createNextModal.createdId}`)
          }}
          onConfirm={() => {
            setCreateNextModal((m) => ({ ...m, show: false }))
            navigate('/company/objects/new', { state: { clientId: createNextModal.createdId } })
          }}
        />
      )}
      {createNextModal.show && createNextModal.entityType === 'objects' && (
        <ConfirmModal
          title="Создать договор?"
          message={<p>Создать договор для этого объекта?</p>}
          cancelLabel="Нет, перейти к карточке объекта"
          confirmLabel="Да, создать договор"
          confirmVariant="primary"
          onCancel={() => {
            setCreateNextModal((m) => ({ ...m, show: false }))
            navigate(`/object/${createNextModal.createdId}`)
          }}
          onConfirm={() => {
            setCreateNextModal((m) => ({ ...m, show: false }))
            navigate('/company/contracts/new', { state: { clientId: createNextModal.created?.client_id, objectId: createNextModal.createdId } })
          }}
        />
      )}
      {createNextModal.show && createNextModal.entityType === 'contracts' && (
        <ConfirmModal
          title="Создать этапы автоматически?"
          message={
            <>
              <p>Для договора с привязанным объектом можно сразу создать три этапа (ЭП, ПД, РД) с бюджетами по настройкам системы.</p>
              <p>Суммы и типы распределения берутся из раздела «Параметры системы».</p>
            </>
          }
          cancelLabel="Нет, только договор"
          confirmLabel={creatingDefaultStages ? 'Создание…' : 'Да, создать этапы'}
          confirmVariant="primary"
          confirmDisabled={creatingDefaultStages}
          error={error}
          onCancel={() => {
            setCreateNextModal((m) => ({ ...m, show: false }))
            navigate(`/contract/${createNextModal.createdId}`)
          }}
          onConfirm={async () => {
            setCreatingDefaultStages(true)
            setError(null)
            try {
              await api.post(
                `/company/contracts/${createNextModal.createdId}/default-stages`,
                { object_id: createNextModal.created?.object_id },
              )
              setCreateNextModal((m) => ({ ...m, show: false }))
              navigate(`/contract/${createNextModal.createdId}`)
            } catch (err) {
              setError(translateApiError(err.response?.data?.error || err.message) || 'Не удалось создать этапы')
            } finally {
              setCreatingDefaultStages(false)
            }
          }}
        />
      )}
    </div>
  )
}

export default EntityForm
